import { Component, computed, effect, inject, signal } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { CommonModule } from "@angular/common";
import { NzButtonModule } from "ng-zorro-antd/button";
import { NzInputModule } from "ng-zorro-antd/input";
import { NzSelectModule } from "ng-zorro-antd/select";
import { NzRadioModule } from "ng-zorro-antd/radio";
import { NzInputNumberModule } from "ng-zorro-antd/input-number";
import { NzUploadModule, NzUploadFile } from "ng-zorro-antd/upload";
import { NzIconModule } from "ng-zorro-antd/icon";
import { NzMessageService } from "ng-zorro-antd/message";
import { NZ_MODAL_DATA, NzModalRef } from "ng-zorro-antd/modal";
import { CORE_DISPUTE_NO_SPECIFIC_MILESTONE, CoreDesiredOutcome, CoreCreateDisputeDto, CoreDisputeType, CoreHeldBackAmountType, DropdownItem, roundMoney } from "@handshake/core-types";
import { CORE_HELD_BACK_AMOUNT_OPTIONS, CoreRaiseDisputeModalConfig, formatDisputeAmount } from "./core-raise-dispute-modal.config";

interface CoreRaiseDisputeModalData extends CoreRaiseDisputeModalConfig {}

@Component({
  selector: "app-core-raise-dispute-modal",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzButtonModule,
    NzInputModule,
    NzSelectModule,
    NzRadioModule,
    NzInputNumberModule,
    NzUploadModule,
    NzIconModule,
  ],
  templateUrl: "./core-raise-dispute-modal.component.html",
  styles: [
    `
      :host ::ng-deep .ant-upload {
        width: 100%;
      }
      :host ::ng-deep .ant-upload-drag {
        background: #fafafa;
        border: 1px dashed #d9d9d9;
        border-radius: 8px;
      }
      :host ::ng-deep .ant-upload-drag:hover {
        border-color: #1890ff;
      }
      /* A long milestone name wraps and the box grows with it, so the full name stays readable on phones (HAN-637) */
      :host ::ng-deep .milestone-in-question-select.ant-select-single:not(.ant-select-customize-input) .ant-select-selector {
        height: auto;
      }
      /* Dropdown list renders outside the modal: long milestone names wrap instead of being cut off (HAN-637) */
      ::ng-deep .milestone-in-question-dropdown .ant-select-item {
        height: auto;
      }
      ::ng-deep .milestone-in-question-dropdown .ant-select-item-option-content {
        white-space: normal;
        overflow-wrap: anywhere;
      }
      :host ::ng-deep .milestone-in-question-select .ant-select-selection-item {
        white-space: normal;
        overflow-wrap: anywhere;
        line-height: 1.5;
        padding-top: 9px;
        padding-bottom: 9px;
      }
      :host ::ng-deep .desired-outcome-select .ant-select-selector {
        min-height: 40px;
      }
      :host ::ng-deep .desired-outcome-select .ant-select-selection-item {
        white-space: normal;
        word-wrap: break-word;
        overflow-wrap: break-word;
        line-height: 1.5;
        padding: 6px 12px;
        display: flex;
        align-items: center;
      }
      :host ::ng-deep .desired-outcome-select .ant-select-selection-placeholder {
        white-space: normal;
        word-wrap: break-word;
        overflow-wrap: break-word;
        line-height: 1.5;
        padding: 6px 12px;
      }
      :host ::ng-deep .desired-outcome-select .ant-select-dropdown {
        max-height: 400px !important;
      }
      :host ::ng-deep .desired-outcome-select .ant-select-dropdown .ant-select-item {
        white-space: normal;
        word-wrap: break-word;
        overflow-wrap: break-word;
        padding: 12px 16px;
        margin: 0;
        line-height: 1.6;
        min-height: 48px;
        height: auto;
        display: flex;
        align-items: center;
      }
      :host ::ng-deep .desired-outcome-select .ant-select-dropdown .ant-select-item:hover {
        background-color: #f5f5f5;
      }
      :host ::ng-deep .desired-outcome-select .ant-select-dropdown .ant-select-item-option-selected {
        background-color: #e6f7ff;
        font-weight: 500;
      }
    `,
  ],
})
export class CoreRaiseDisputeModalComponent {
  readonly modalRef = inject(NzModalRef);
  readonly fb = inject(FormBuilder);
  readonly nzMessage = inject(NzMessageService);
  readonly data = inject<CoreRaiseDisputeModalData>(NZ_MODAL_DATA);

  currentPage = signal(0); // 0 = Page 1 (Form), 1 = Page 2 (Confirmation)
  isSubmitting = signal(false);
  files = signal<File[]>([]);
  desiredOutcomeValue = signal<CoreDesiredOutcome | null>(null);
  milestoneInQuestionValue = signal<string | null>(null);
  heldBackAmountTypeValue = signal<CoreHeldBackAmountType | null>(null);

  form: FormGroup = this.fb.group({
    // "Which milestone is this about?" — required for everyone (HAN-637); no default, the user must pick
    milestoneInQuestion: [null as string | null, [Validators.required]],
    issueDescription: ["", [Validators.required]],
    desiredOutcome: ["", [Validators.required]],
    reason: [""],
    clientReleaseReason: [null as string | null],
    issueType: [null as string | null],
    // "How much is being held back?" — business "Client to release funds" only (HAN-692)
    heldBackAmountType: [null as CoreHeldBackAmountType | null],
    heldBackAmount: [null as number | null],
  });

  /** "Which milestone is this about?" choices (HAN-637). */
  readonly milestoneOptions = this.data.milestoneOptions ?? [];

  /** "Type of issue" choices — only given for clients of ID companies (HAN-638); empty means the field is hidden. */
  readonly issueTypeOptions = this.data.issueTypeOptions ?? [];

  // Convert options to dropdown items
  dropdownItems: DropdownItem[] = this.data.desiredOutcomeOptions.map((opt) => ({
    code: opt.value,
    label: opt.label,
  }));

  // Get selected option config - now properly reactive
  selectedOption = computed(() => {
    const selectedValue = this.desiredOutcomeValue();
    return this.data.desiredOutcomeOptions.find((opt) => opt.value === selectedValue);
  });

  /** HAN-692: the selected outcome asks "How much is being held back?". */
  showHeldBackQuestion = computed(() => !!this.selectedOption()?.heldBackAmountQuestion);

  /** HAN-692: balance in escrow of the picked milestone; null when "not about one specific milestone" (or none picked yet). */
  milestoneHeldInEscrow = computed(() => {
    const milestoneId = this.milestoneInQuestionValue();
    if (!milestoneId || milestoneId === CORE_DISPUTE_NO_SPECIFIC_MILESTONE) return null;
    return roundMoney(this.data.milestoneHeldInEscrow?.[milestoneId] ?? 0);
  });

  /** "Full balance" is only offered for one specific milestone that has money in escrow. */
  canChooseFullBalance = computed(() => (this.milestoneHeldInEscrow() ?? 0) > 0);

  /** The dropdown is hidden when "Other" is the only path; the amount and reason fields then show straight away. */
  showHeldBackTypeSelect = computed(() => this.showHeldBackQuestion() && this.canChooseFullBalance());
  readonly heldBackAmountOptions = CORE_HELD_BACK_AMOUNT_OPTIONS;

  isFullBalance = computed(() => this.showHeldBackQuestion() && this.heldBackAmountTypeValue() === CoreHeldBackAmountType.FULL_BALANCE);
  showHeldBackOther = computed(() => this.showHeldBackQuestion() && this.heldBackAmountTypeValue() === CoreHeldBackAmountType.OTHER);

  /** Reason box: shown for outcomes that need one, except "Client to release funds" with "Full balance" (the issue box already says why). */
  showReasonBox = computed(() => !!this.selectedOption()?.requiresReason && (!this.showHeldBackQuestion() || this.showHeldBackOther()));

  readonly currency = this.data.currency ?? null;
  formatAmount(amount: number | null | undefined): string {
    return formatDisputeAmount(amount, this.currency);
  }

  /** When "Full balance" can't be chosen (no specific milestone, or SGD 0 in escrow), the answer is "Other". */
  private heldBackTypeSyncEffect = effect(() => {
    if (!this.showHeldBackQuestion() || this.canChooseFullBalance()) return;
    const control = this.form.get("heldBackAmountType");
    if (control?.value !== CoreHeldBackAmountType.OTHER) {
      control?.setValue(CoreHeldBackAmountType.OTHER);
    }
  });

  // Effect to update reason field validation based on selected option
  private reasonValidationEffect = effect(() => {
    const option = this.selectedOption();
    const reasonControl = this.form.get("reason");

    if (this.showReasonBox()) {
      reasonControl?.setValidators([Validators.required]);
    } else {
      reasonControl?.clearValidators();
    }
    reasonControl?.updateValueAndValidity();

    // Client's-reason question is only asked (and required) when the selected option defines it
    const clientReasonControl = this.form.get("clientReleaseReason");
    if (option?.clientReleaseReasonQuestion) {
      clientReasonControl?.setValidators([Validators.required]);
    } else {
      clientReasonControl?.clearValidators();
    }
    clientReasonControl?.updateValueAndValidity();

    // HAN-692: amount held back — required choice; "Other" needs an amount above 0 and, for one milestone, within its balance
    const heldBackTypeControl = this.form.get("heldBackAmountType");
    heldBackTypeControl?.setValidators(this.showHeldBackQuestion() ? [Validators.required] : []);
    heldBackTypeControl?.updateValueAndValidity({ emitEvent: false });

    const heldBackAmountControl = this.form.get("heldBackAmount");
    const balance = this.milestoneHeldInEscrow();
    heldBackAmountControl?.setValidators(
      this.showHeldBackOther() ? [Validators.required, Validators.min(0.01), ...(balance != null ? [Validators.max(balance)] : [])] : []
    );
    heldBackAmountControl?.updateValueAndValidity({ emitEvent: false });
  });

  constructor() {
    // "Type of issue" is required only when the form shows it
    if (this.issueTypeOptions.length > 0) {
      this.form.get("issueType")?.setValidators([Validators.required]);
      this.form.get("issueType")?.updateValueAndValidity();
    }

    // Watch desired outcome changes and update signal
    this.form.get("desiredOutcome")?.valueChanges.subscribe((value) => {
      this.desiredOutcomeValue.set(value);
      // Reset reason when outcome changes
      this.form.get("reason")?.setValue("");
      this.form.get("clientReleaseReason")?.setValue(null);
      this.form.get("heldBackAmountType")?.setValue(null);
      this.form.get("heldBackAmount")?.setValue(null);
    });
    this.milestoneInQuestionValue.set(this.form.get("milestoneInQuestion")?.value ?? null);
    this.form.get("milestoneInQuestion")?.valueChanges.subscribe((value) => this.milestoneInQuestionValue.set(value));
    this.form.get("heldBackAmountType")?.valueChanges.subscribe((value) => this.heldBackAmountTypeValue.set(value));
  }

  beforeUpload = (file: NzUploadFile, fileList: NzUploadFile[]): boolean => {
    const actualFile = file.originFileObj || (file as any);
    if (!actualFile || !(actualFile instanceof File)) {
      return false;
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif", "application/pdf"];

    if (actualFile.size > maxSize) {
      this.nzMessage.error(`${actualFile.name}: File size must be less than 10MB`);
      return false;
    }

    if (!allowedTypes.includes(actualFile.type)) {
      this.nzMessage.error(`${actualFile.name}: File type must be JPEG, PNG, PDF, or GIF`);
      return false;
    }

    this.files.update((current) => {
      if (!current.find((f) => f.name === actualFile.name && f.size === actualFile.size)) {
        return [...current, actualFile];
      }
      return current;
    });

    return false; // Prevent auto upload
  };

  removeFile(index: number): void {
    this.files.update((current) => current.filter((_, i) => i !== index));
  }

  next(): void {
    if (this.currentPage() === 0) {
      // Validate form before proceeding
      if (this.form.invalid) {
        this.form.markAllAsTouched();
        return;
      }
      this.currentPage.set(1);
    }
  }

  back(): void {
    if (this.currentPage() === 1) {
      this.currentPage.set(0);
    }
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSubmitting.set(true);

    const formValue = this.form.value;
    const disputeData: CoreCreateDisputeDto = {
      projectMilestoneId: this.data.projectMilestoneId,
      milestoneInQuestionId: formValue.milestoneInQuestion,
      disputeType: CoreDisputeType.INITIAL_ATTEMPT, // Default, can be made configurable
      describeIssue: formValue.issueDescription,
      desiredOutcome: formValue.desiredOutcome,
      // "Full balance" has no reason box, so nothing typed earlier under "Other" is sent
      expectation: this.showReasonBox() ? formValue.reason || undefined : undefined,
      // Only send when the selected outcome asked for it (switching outcome resets the answer)
      clientReleaseReason: this.selectedOption()?.clientReleaseReasonQuestion ? formValue.clientReleaseReason || undefined : undefined,
      // Only sent when the form asked for it (clients of ID companies)
      issueType: this.issueTypeOptions.length > 0 ? formValue.issueType || undefined : undefined,
      // HAN-692: for "Full balance" only the choice is sent — the server works out the amount
      heldBackAmountType: this.showHeldBackQuestion() ? formValue.heldBackAmountType || undefined : undefined,
      heldBackAmount: this.showHeldBackOther() && formValue.heldBackAmount != null ? Number(formValue.heldBackAmount) : undefined,
    };

    this.data.submitRequest(disputeData, this.files()).subscribe({
      next: () => {
        this.isSubmitting.set(false);
        this.modalRef.close({ success: true });
      },
      error: (error) => {
        this.isSubmitting.set(false);
        this.nzMessage.error(error?.error?.message || "Failed to submit dispute");
      },
    });
  }

  onCancel(): void {
    this.modalRef.close();
  }
}
